<script setup>
import { computed, onMounted, ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { useDebounceFn } from '@vueuse/core';
import AutomationAPI from 'dashboard/api/automation';
import { useAccount } from 'dashboard/composables/useAccount';
import { useAccountTimeZone } from 'dashboard/composables/useAccountTimeZone';
import Input from 'dashboard/components-next/input/Input.vue';
import Icon from 'dashboard/components-next/icon/Icon.vue';
import Spinner from 'dashboard/components-next/spinner/Spinner.vue';
import NextButton from 'dashboard/components-next/button/Button.vue';
import {
  SIMULATION_STATUS_WOULD_EXECUTE,
  SKIP_REASONS,
  apiErrorMessage,
  formatCalendarDate,
} from '../dateTrigger';

const props = defineProps({
  // Corpo do simulate no formato do criar/editar, lido a cada chamada para refletir o formulário.
  getPayload: {
    type: Function,
    required: true,
  },
});

const emit = defineEmits(['close']);

// A data escolhida fica com quem abre a simulação, para sobreviver a fechar e abrir de novo.
const referenceDate = defineModel('referenceDate', {
  type: String,
  default: '',
});

const { t, locale } = useI18n();
const { accountScopedRoute } = useAccount();
const { today } = useAccountTimeZone();

const isLoading = ref(false);
const result = ref(null);
const errorMessage = ref('');

const formatDate = isoDate => formatCalendarDate(isoDate, locale.value);
const formatCount = count =>
  new Intl.NumberFormat(locale.value.replace('_', '-')).format(count);

const MIN_REFERENCE_YEAR = 1900;
const SIMULATE_DEBOUNCE_MS = 400;

// O campo de data emite valores parciais enquanto se digita o ano (0020-10-19).
const isCompleteDate = value =>
  /^\d{4}-\d{2}-\d{2}$/.test(value) &&
  Number(value.slice(0, 4)) >= MIN_REFERENCE_YEAR &&
  !Number.isNaN(Date.parse(value));

let lastSimulatedDate = '';

const simulate = async () => {
  lastSimulatedDate = referenceDate.value;
  isLoading.value = true;
  errorMessage.value = '';
  try {
    const { data } = await AutomationAPI.simulateDateTrigger({
      ...props.getPayload(),
      reference_date: referenceDate.value,
    });
    result.value = data;
  } catch (error) {
    result.value = null;
    errorMessage.value =
      apiErrorMessage(error) || t('AUTOMATION.DATE_TRIGGER.SIMULATION.ERROR');
  } finally {
    isLoading.value = false;
  }
};

const skippedRows = computed(() =>
  SKIP_REASONS.filter(reason => result.value?.skipped?.[reason]).map(
    reason => ({
      reason,
      label: t(`AUTOMATION.DATE_TRIGGER.SKIP_REASONS.${reason.toUpperCase()}`),
      count: result.value.skipped[reason],
    })
  )
);

const sampleStatusLabel = sample =>
  sample.status === SIMULATION_STATUS_WOULD_EXECUTE
    ? t('AUTOMATION.DATE_TRIGGER.SIMULATION.WOULD_EXECUTE')
    : t(
        `AUTOMATION.DATE_TRIGGER.SKIP_REASONS.${sample.skip_reason.toUpperCase()}`
      );

const contactRoute = contactId =>
  accountScopedRoute('contacts_edit', { contactId });

const simulateDebounced = useDebounceFn(simulate, SIMULATE_DEBOUNCE_MS);

watch(referenceDate, value => {
  if (isCompleteDate(value) && value !== lastSimulatedDate) simulateDebounced();
});
onMounted(() => {
  if (!referenceDate.value) referenceDate.value = today();
  simulate();
});
</script>

<template>
  <div
    data-test="date-simulation"
    class="flex flex-col gap-3 p-3 rounded-xl bg-n-alpha-1"
  >
    <div class="flex flex-wrap items-center justify-between gap-3">
      <div class="flex flex-wrap items-center gap-3">
        <span class="text-sm font-medium text-n-slate-12">
          {{ $t('AUTOMATION.DATE_TRIGGER.SIMULATION.TITLE') }}
        </span>
        <span class="text-sm text-n-slate-11">
          {{ $t('AUTOMATION.DATE_TRIGGER.SIMULATION.REFERENCE_DATE') }}
        </span>
        <div class="w-40">
          <Input
            v-model="referenceDate"
            data-test="reference-date"
            type="date"
          />
        </div>
        <Spinner v-if="isLoading" :size="16" class="text-n-slate-10" />
      </div>
      <NextButton
        icon="i-lucide-x"
        slate
        ghost
        xs
        :title="$t('AUTOMATION.DATE_TRIGGER.SIMULATION.CLOSE')"
        @click="emit('close')"
      />
    </div>

    <p
      v-if="errorMessage"
      data-test="simulation-error"
      class="mb-0 text-sm text-n-ruby-11"
    >
      {{ errorMessage }}
    </p>

    <template v-else-if="result">
      <p
        v-if="result.blocked_by_limit"
        data-test="simulation-blocked"
        class="flex items-start gap-2 p-3 mb-0 text-sm rounded-lg bg-n-ruby-3 text-n-ruby-12"
      >
        <Icon icon="i-lucide-triangle-alert" class="mt-0.5 shrink-0" />
        {{
          $t('AUTOMATION.DATE_TRIGGER.SIMULATION.BLOCKED', {
            count: formatCount(result.matched_count),
            limit: formatCount(result.limit),
          })
        }}
      </p>
      <template v-else>
        <p
          data-test="simulation-summary"
          class="mb-0 text-sm font-medium text-n-slate-12"
        >
          {{
            $t('AUTOMATION.DATE_TRIGGER.SIMULATION.SUMMARY', {
              date: formatDate(result.reference_date),
              count: formatCount(result.would_execute_count),
              total: formatCount(result.matched_count),
            })
          }}
        </p>
        <ul
          v-if="skippedRows.length"
          data-test="simulation-skipped"
          class="flex flex-col gap-1 p-0 m-0 list-none"
        >
          <li
            v-for="row in skippedRows"
            :key="row.reason"
            class="text-xs text-n-slate-11"
          >
            {{ row.label }}: {{ formatCount(row.count) }}
          </li>
        </ul>
      </template>

      <p
        v-if="result.offer"
        data-test="simulation-offer"
        class="mb-0 text-xs text-n-slate-12"
      >
        {{
          $t(
            'AUTOMATION.DATE_TRIGGER.OFFER.RECIPIENTS',
            { count: formatCount(result.offer.recipients_count) },
            result.offer.recipients_count
          )
        }}
      </p>

      <p
        v-if="result.invalid_date_count > 0"
        data-test="simulation-invalid-dates"
        class="mb-0 text-xs text-n-amber-11"
      >
        {{
          $t(
            'AUTOMATION.DATE_TRIGGER.SIMULATION.INVALID_DATES',
            { count: formatCount(result.invalid_date_count) },
            result.invalid_date_count
          )
        }}
      </p>

      <div v-if="result.samples.length" class="overflow-x-auto">
        <table data-test="simulation-samples" class="w-full text-xs">
          <thead>
            <tr class="text-start text-n-slate-11">
              <th class="py-1 pe-3 font-medium text-start">
                {{ $t('AUTOMATION.DATE_TRIGGER.SIMULATION.COLUMNS.RECORD') }}
              </th>
              <th class="py-1 pe-3 font-medium text-start">
                {{ $t('AUTOMATION.DATE_TRIGGER.SIMULATION.COLUMNS.CONTACT') }}
              </th>
              <th class="py-1 pe-3 font-medium text-start">
                {{
                  $t('AUTOMATION.DATE_TRIGGER.SIMULATION.COLUMNS.SOURCE_DATE')
                }}
              </th>
              <th class="py-1 pe-3 font-medium text-start">
                {{
                  $t('AUTOMATION.DATE_TRIGGER.SIMULATION.COLUMNS.OCCURRENCE')
                }}
              </th>
              <th class="py-1 font-medium text-start">
                {{ $t('AUTOMATION.DATE_TRIGGER.SIMULATION.COLUMNS.STATUS') }}
              </th>
            </tr>
          </thead>
          <tbody>
            <tr
              v-for="sample in result.samples"
              :key="`${sample.target_type}-${sample.target_id}`"
              class="border-t border-n-weak text-n-slate-12"
            >
              <td class="py-1 pe-3">{{ sample.label }}</td>
              <td class="py-1 pe-3">
                <router-link
                  v-if="sample.contact_id"
                  :to="contactRoute(sample.contact_id)"
                  class="text-n-blue-11"
                >
                  {{ sample.contact_name }}
                </router-link>
              </td>
              <td class="py-1 pe-3">{{ formatDate(sample.source_date) }}</td>
              <td class="py-1 pe-3">
                {{ formatDate(sample.occurrence_date) }}
              </td>
              <td class="py-1">{{ sampleStatusLabel(sample) }}</td>
            </tr>
          </tbody>
        </table>
      </div>
      <p
        v-else-if="!result.blocked_by_limit"
        class="mb-0 text-xs text-n-slate-11"
      >
        {{ $t('AUTOMATION.DATE_TRIGGER.SIMULATION.EMPTY') }}
      </p>

      <p data-test="simulation-footer" class="mb-0 text-xs text-n-slate-10">
        {{ $t('AUTOMATION.DATE_TRIGGER.SIMULATION.FOOTER') }}
      </p>
    </template>
  </div>
</template>
